import PropTypes from "prop-types";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Radio, Tooltip } from "top-component-library";
import WeeklyScheduleInputs from "../../components/WeeklyScheduleInputs";
import { ORDERS_SCHEDULE_OPTIONS } from "../../constants";
import { DAY_ORDER } from "../../scheduleUtils";
import { TwoColumnRow } from "../../styles";
import DateRangeRestrictions from "./DateRangeRestrictions";

const scheduleValues = (schedule) => {
  const values = {};
  for (const day of DAY_ORDER) values[day] = schedule?.[day]?.customer_input ?? "";
  return values;
};

const radioStyle = { display: "block", marginBottom: 8 };

const TerminalNote = () => (
  <p>
    <i>
      Note: regardless of these settings, the TOP by DoorDash Terminal must have ordering allowed and be online, in
      order to receive orders. For specific unusual use cases the latter behavior can be changed by support request.
    </i>
  </p>
);

// Section A: when guests can place orders. Without order-ahead it's an
// inherit/schedule radio; with order-ahead it's the ordering window plus the
// delivery/pickup window (inherited read-only when the menu is merged into
// another). Each active schedule carries its allow/exclude date-range lists.
const OrderTimingSection = ({
  menu,
  allMenus,
  ordersSchedule,
  onOrdersScheduleChange,
  onScheduleDayChange,
  onDateRangesChange,
}) => {
  if (!menu.allow_order_ahead) {
    return (
      <div>
        <TerminalNote />
        <Radio.Group value={ordersSchedule} onChange={(event) => onOrdersScheduleChange(event.target.value)}>
          {ORDERS_SCHEDULE_OPTIONS.map((option) => (
            <Radio key={option.value} value={option.value} style={radioStyle}>
              {option.label}
            </Radio>
          ))}
        </Radio.Group>
        {ordersSchedule === "useschedule" && (
          <div className="margin-top-2">
            <h4>Menu Available Times</h4>
            <WeeklyScheduleInputs
              idPrefix={`${menu.menuId}-active`}
              values={scheduleValues(menu.active_schedule)}
              onChange={(day, value) => onScheduleDayChange("active_schedule", day, value)}
            />
            <DateRangeRestrictions menu={menu} scheduleType="active" onChange={onDateRangesChange} />
          </div>
        )}
      </div>
    );
  }

  const parentMenu = menu.combine_into_id ? allMenus.find((other) => other.menuId === menu.combine_into_id) : null;

  return (
    <div>
      <TerminalNote />
      <TwoColumnRow>
        <div>
          <h4>Allowed Ordering Windows</h4>
          <WeeklyScheduleInputs
            idPrefix={`${menu.menuId}-visible`}
            values={scheduleValues(menu.visible_schedule)}
            onChange={(day, value) => onScheduleDayChange("visible_schedule", day, value)}
          />
          <DateRangeRestrictions menu={menu} scheduleType="visible" onChange={onDateRangesChange} />
        </div>
        <div>
          <h4>
            Allowed Delivery/Pickup Times{" "}
            <Tooltip title="If this menu is set to be displayed as part of another menu, you will be shown the Allowed Delivery / Pickup Times of the other menu. To edit these times, set this menu to be displayed as its own tab.">
              <QuestionCircleOutlined style={{ padding: 4 }} />
            </Tooltip>
          </h4>
          {parentMenu ? (
            <>
              <p>
                <i>
                  This menu is displayed as part of {parentMenu.name_for_owner}, so it follows that menu&apos;s allowed
                  delivery/pickup times. Set it to its own tab to edit these.
                </i>
              </p>
              <WeeklyScheduleInputs
                idPrefix={`${menu.menuId}-active-inherited`}
                values={scheduleValues(parentMenu.active_schedule)}
                disabled
                onChange={() => {}}
              />
            </>
          ) : (
            <>
              <WeeklyScheduleInputs
                idPrefix={`${menu.menuId}-active`}
                values={scheduleValues(menu.active_schedule)}
                onChange={(day, value) => onScheduleDayChange("active_schedule", day, value)}
              />
              <DateRangeRestrictions menu={menu} scheduleType="active" onChange={onDateRangesChange} />
            </>
          )}
        </div>
      </TwoColumnRow>
    </div>
  );
};

OrderTimingSection.propTypes = {
  menu: PropTypes.object.isRequired,
  allMenus: PropTypes.arrayOf(PropTypes.object).isRequired,
  ordersSchedule: PropTypes.string.isRequired,
  onOrdersScheduleChange: PropTypes.func.isRequired,
  onScheduleDayChange: PropTypes.func.isRequired,
  onDateRangesChange: PropTypes.func.isRequired,
};

export default OrderTimingSection;
